import React from 'react';
import { classNames } from '@/lib/utils';
const Tooltip = React.lazy(() => import('./TooltipWrapper'));
import QuestionMarkIcon from '@public/img/icons/question-mark.svg';
import { ArrowSmallUpIcon } from '@heroicons/react/20/solid';

interface Props {
  id?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  titleClassName?: string;
  title?: string;
  subtitle?: string;
  value?: string;
  tooltip?: string;
  icon?: React.ReactNode;
  rightSideWrapper?: React.ReactNode;
  badge?: React.ReactNode;
}

const CardTitle = ({ value, title, subtitle, badge, className }) => (
  <div className={classNames("flex items-center px-3 py-3 sm:px-3", className)}>
    <div>
      <h2 className="mb-1 font-medium tracking-tight text-gray-500 text-md font-dashboard">{title}</h2>
      {value && (
        <h3 className="text-3xl font-bold text-gray-900">
          {value}
          {badge && <span className="ml-2">{badge}</span>}
          <div className="ml-4 mr-4 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 align-middle text-sm font-medium text-green-600 md:mt-2 lg:mt-0">
            <ArrowSmallUpIcon className="-ml-1 mr-0.5 h-5 w-5 flex-shrink-0 rotate-45 self-center text-green-500" />
            12%
          </div>
        </h3>
      )}
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
  </div>
);

const TooltipTitle = ({ tooltip, title, badge }) => {
  const TitleComponent = (
    <h2 className="inline-flex items-center text-xl font-bold tracking-tight text-gray-800 font-dashboard">
      {title}
      {/* <QuestionMarkIcon className="ignore ml-2 mt-[2px] w-[18px] h-[18px] self-center box-border relative flex-none w-4.5 h-4.5 fill-gray-300" /> */}
      <svg className='ignore ml-2 mt-[2px] w-[18px] h-[18px] self-center box-border relative flex-none w-4.5 h-4.5 fill-gray-300' stroke="none" xmlns="http://www.w3.org/2000/svg" strokeWidth="1.7px" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" viewBox="0 0 24 24" style={{ boxSizing: "border-box", position: "relative", flex: "0 0 auto" }}>
        <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12.0023 18.458H12.0115L12.0023 18.4488C12.758 18.4488 13.385 17.8236 13.3875 17.0687C13.3875 17.0702 13.3875 17.0718 13.3875 17.0733V17.0641C13.3875 17.0656 13.3875 17.0672 13.3875 17.0687C13.385 16.3046 12.758 15.6886 12.0023 15.6886C11.2357 15.6886 10.617 16.3071 10.617 17.0733C10.617 17.8303 11.2357 18.4488 12.0023 18.458ZM13.4891 12.8361V12.8453L13.485 12.8378C13.4863 12.8372 13.4877 12.8367 13.4891 12.8361ZM12.935 13.6762C12.9258 13.3084 13.1456 12.9773 13.485 12.8378L13.443 12.7623C14.7913 12.1715 15.6686 10.8514 15.6686 9.38357C15.6686 7.35266 14.0063 5.69101 11.9746 5.70024C9.9428 5.70024 8.28045 7.36189 8.28969 9.40203L8.29892 9.41126C8.29892 9.91899 8.70527 10.3344 9.22245 10.3344C9.73039 10.3344 10.146 9.91899 10.146 9.41126C10.146 8.67275 10.58 8.00809 11.2542 7.72192C12.187 7.31574 13.2767 7.74038 13.6923 8.68198C14.0987 9.61435 13.6738 10.7037 12.7411 11.1191V11.1283C11.7252 11.5622 11.0695 12.5592 11.0787 13.6669L11.088 13.6762C11.088 14.1839 11.4943 14.5993 12.0115 14.5993C12.5194 14.5993 12.935 14.1839 12.935 13.6762Z" />
      </svg>
      <span className="ml-2">{badge}</span>
    </h2>
  );

  return (
    <React.Suspense fallback={TitleComponent}>
      <Tooltip content={tooltip} placement="auto" animation="duration-300" className="absolute left-10 my-auto ml-1 max-w-[500px] shadow-xl">
        {TitleComponent}
      </Tooltip>
    </React.Suspense>
  );
};


const Card: React.FC<Props> = ({
  children, className, title, subtitle, value, tooltip, icon, rightSideWrapper, badge, titleClassName, id, style
}) => (
  <div className={classNames('card shadow-keesy-card divide-y border mb-6 md:mb-8 divide-gray-200 overflow-hidden border-gray-100 bg-white', className)} id={id} style={style}>
    {title && value ? (
      <CardTitle value={value} title={title} subtitle={subtitle} badge={badge} className={titleClassName} />
    ) : title ? (
      <div className="relative inline-flex items-center w-full px-3 py-3 sm:px-3">
        {rightSideWrapper && <div className="absolute top-0 right-0 mt-4 mr-3">{rightSideWrapper}</div>}
        {icon && icon}
        {tooltip ? <TooltipTitle tooltip={tooltip} title={title} badge={badge} /> :
          <h2 className={classNames("inline-flex items-center text-xl font-bold tracking-tight text-gray-800 font-dashboard", titleClassName)}>
            {title}
            {badge && <React.Fragment>{badge}</React.Fragment>}
          </h2>}
      </div>
    ) : null}
    <div className="px-2 pt-1">{children}</div>
  </div>
);

export default Card;
